import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AxiosError } from 'axios';
import { Loader2, Upload, ImageIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNotifications } from '@/hooks/useNotifications';
import { useUpload } from '@/hooks/useUpload';
import { useCreateCoupon } from '@/hooks/useCoupons';

const couponSchema = z
  .object({
    title: z
      .string()
      .min(1, 'Title is required')
      .max(100, 'Title cannot exceed 100 characters')
      .refine((val) => !val.startsWith(' '), 'Title cannot start with a space'),
    description: z
      .string()
      .min(1, 'Description is required')
      .max(500, 'Description cannot exceed 500 characters')
      .refine((val) => !val.startsWith(' '), 'Description cannot start with a space'),
    discountLabel: z
      .string()
      .min(1, 'Discount label is required')
      .max(50, 'Discount label cannot exceed 50 characters')
      .refine((val) => !val.startsWith(' '), 'Discount label cannot start with a space'),
    image: z.string().min(1, 'Image is required'),
    pointsCost: z.preprocess(
      (val) => (val === '' || val === undefined ? undefined : Number(val)),
      z
        .number({ required_error: 'Coupon Value is required', invalid_type_error: 'Must be a number' })
        .min(0, 'Coupon Value must be 0 or more')
        .max(1000000, 'Coupon Value cannot exceed 1,000,000')
    ),
    totalCount: z.preprocess(
      (val) => (val === '' || val === undefined ? undefined : Number(val)),
      z
        .number({ required_error: 'Total Count is required', invalid_type_error: 'Must be a number' })
        .min(1, 'Total Count must be at least 1')
        .max(1000000, 'Total Count cannot exceed 1,000,000')
    ),
    validTill: z
      .string()
      .min(1, 'Valid till date is required')
      .refine((val) => {
        const selected = new Date(val);
        return !isNaN(selected.getTime()) && selected > new Date();
      }, 'Valid till date must be in the future'),
    discountType: z.enum(['Percentage', 'Static']),
    discountValue: z.preprocess(
      (val) => (val === '' || val === undefined ? undefined : Number(val)),
      z
        .number({ required_error: 'Discount Value is required', invalid_type_error: 'Must be a number' })
        .min(0, 'Discount Value must be 0 or more')
        .max(1000000, 'Discount Value cannot exceed 1,000,000')
    ),
  })
  .superRefine((data, ctx) => {
    if (data.discountType === 'Percentage' && data.discountValue > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Percentage discount cannot exceed 100%',
        path: ['discountValue'],
      });
    }
  });

type CouponFormData = z.infer<typeof couponSchema>;

const emptyDefaults: CouponFormData = {
  title: '',
  description: '',
  discountLabel: '',
  image: '',
  pointsCost: '' as any,
  totalCount: '' as any,
  validTill: '',
  discountType: 'Percentage',
  discountValue: '' as any,
};

interface CouponFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantId: number;
}

export function CouponFormDialog({ open, onOpenChange, merchantId }: CouponFormDialogProps) {
  const { success, error: showError } = useNotifications();
  const { mutate: createCoupon, isPending: isCreating } = useCreateCoupon();
  const { mutate: uploadImage, isPending: isUploading } = useUpload();
  const uploadErrorMessage = (err: AxiosError<{ message?: string }>) =>
    err.response?.data?.message || 'Image upload failed';
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CouponFormData>({
    resolver: zodResolver(couponSchema),
    defaultValues: emptyDefaults,
  });

  const watchDiscountType = watch('discountType');

  useEffect(() => {
    if (!open) return;
    reset(emptyDefaults);
    setImagePreview(null);
  }, [open, reset]);

  const validateImage = (
    file: File,
    maxSizeMB: number,
    minWidth: number,
    minHeight: number
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      // Size check
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        showError(`Image file size must be less than ${maxSizeMB} MB.`);
        resolve(false);
        return;
      }
      if (file.size < 2 * 1024) {
        showError(`Image file size must be at least 2 KB.`);
        resolve(false);
        return;
      }

      // Resolution check
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        if (img.naturalWidth < minWidth || img.naturalHeight < minHeight) {
          showError(`Image resolution must be at least ${minWidth}x${minHeight} px.`);
          resolve(false);
        } else {
          resolve(true);
        }
      };
      img.onerror = () => {
        showError(`Invalid image file.`);
        resolve(false);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageSelect = async (file: File) => {
    const isValid = await validateImage(file, 1, 500, 500);
    if (!isValid) return;

    setImagePreview(URL.createObjectURL(file));
    uploadImage(file, {
      onSuccess: (result) => {
        setValue('image', result.meta.filename, { shouldValidate: true });
        success('Image uploaded');
      },
      onError: (err) => {
        showError(uploadErrorMessage(err));
        setImagePreview(null);
      },
    });
  };

  const onSubmit = (data: CouponFormData) => {
    const payload = {
      merchantId,
      ...data,
      minAmount: 0,
      validTill: new Date(data.validTill).toISOString(),
    };

    createCoupon(payload, {
      onSuccess: () => {
        success('Coupon created successfully');
        onOpenChange(false);
      },
      onError: (err) => {
        showError(err.response?.data?.message || 'Failed to create coupon');
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="text-center sm:text-center">
          <DialogTitle className="text-center text-xl font-bold">Add Coupon</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label>Image</Label>
              <span className="text-[10px] font-semibold text-primary uppercase">
                MIN. 500X500 PX | 2 KB - 1 MB
              </span>
            </div>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-dashed border-input bg-muted hover:border-muted-foreground/50"
              >
                {imagePreview ? (
                  <img src={imagePreview} alt="" className="h-full w-full object-cover" />
                ) : (
                  <ImageIcon className="h-6 w-6 text-muted-foreground" />
                )}
              </button>
              <div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isUploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {isUploading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-2 h-4 w-4" />
                  )}
                  {isUploading ? 'Uploading...' : 'Upload image'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageSelect(file);
                  }}
                />
                {errors.image && (
                  <p className="mt-1 text-sm text-destructive">{errors.image.message}</p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              {...register('title')}
              className={errors.title ? 'border-destructive' : ''}
            />
            {errors.title && <p className="text-sm text-destructive">{errors.title.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              {...register('description')}
              className={errors.description ? 'border-destructive' : ''}
            />
            {errors.description && (
              <p className="text-sm text-destructive">{errors.description.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="discountLabel">Discount Label</Label>
            <Input
              id="discountLabel"
              placeholder="e.g. 20% OFF"
              {...register('discountLabel')}
              className={errors.discountLabel ? 'border-destructive' : ''}
            />
            {errors.discountLabel && (
              <p className="text-sm text-destructive">{errors.discountLabel.message}</p>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="discountType">Discount Type</Label>
              <Select
                value={watchDiscountType || 'Percentage'}
                onValueChange={(value) => setValue('discountType', value as any, { shouldValidate: true })}
              >
                <SelectTrigger id="discountType">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Percentage">Percentage</SelectItem>
                  <SelectItem value="Static">Static</SelectItem>
                </SelectContent>
              </Select>
              {errors.discountType && (
                <p className="text-sm text-destructive">{errors.discountType.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="discountValue">Discount Value</Label>
              <Input
                id="discountValue"
                type="number"
                step="any"
                min={0}
                max={watchDiscountType === 'Percentage' ? 100 : 1000000}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder={watchDiscountType === 'Percentage' ? 'e.g. 20 (max 100%)' : 'e.g. 500'}
                {...register('discountValue')}
                className={`[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                  errors.discountValue ? 'border-destructive' : ''
                }`}
              />
              {errors.discountValue && (
                <p className="text-sm text-destructive">{errors.discountValue.message}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="pointsCost">Coupon Value</Label>
              <Input
                id="pointsCost"
                type="number"
                step="any"
                min={0}
                max={1000000}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="e.g. 50"
                {...register('pointsCost')}
                className={`[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                  errors.pointsCost ? 'border-destructive' : ''
                }`}
              />
              {errors.pointsCost && (
                <p className="text-sm text-destructive">{errors.pointsCost.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="totalCount">Total Count</Label>
              <Input
                id="totalCount"
                type="number"
                min={1}
                max={1000000}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="e.g. 100"
                {...register('totalCount')}
                className={`[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                  errors.totalCount ? 'border-destructive' : ''
                }`}
              />
              {errors.totalCount && (
                <p className="text-sm text-destructive">{errors.totalCount.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="validTill">Valid Till</Label>
            <Input
              id="validTill"
              type="datetime-local"
              min={new Date().toISOString().slice(0, 16)}
              {...register('validTill')}
              className={errors.validTill ? 'border-destructive' : ''}
            />
            {errors.validTill && (
              <p className="text-sm text-destructive">{errors.validTill.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating || isUploading}>
              {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Coupon
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
